import "./NativeAppInstall.css";

import { Controller } from "@hotwired/stimulus";
import {
    shouldInterceptNativeAppAction,
    shouldInterceptNativeAppActionKey,
    usesDirectNativeAppInstall
} from "./NativeAppInstallUi";

export default class NativeAppInstallController extends Controller<HTMLElement> {
    static targets = ["overlay", "panel", "initialFocus", "qrCode"];

    declare readonly overlayTarget: HTMLElement;
    declare readonly panelTarget: HTMLElement;
    declare readonly initialFocusTarget: HTMLElement;
    declare readonly qrCodeTargets: HTMLImageElement[];

    private previouslyFocusedElement: HTMLElement | null = null;
    private readonly handleDocumentClick = (event: MouseEvent): void => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        if (shouldInterceptNativeAppAction(target, event)) {
            event.preventDefault();
            event.stopPropagation();
            this.open();
        }
    };

    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        const target = event.target;
        if (target instanceof Element && shouldInterceptNativeAppActionKey(target, event)) {
            event.preventDefault();
            event.stopPropagation();
            this.open();
            return;
        }

        if (this.overlayTarget.hidden) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            this.close();
            return;
        }

        if (event.key !== "Tab") {
            return;
        }

        this.keepFocusInsideDialog(event);
    };

    connect(): void {
        this.overlayTarget.dataset.nativeAppInstallPlatform = usesDirectNativeAppInstall(
            navigator.userAgent,
            navigator.maxTouchPoints
        ) ? "mobile" : "desktop";
        document.addEventListener("click", this.handleDocumentClick, true);
        document.addEventListener("keydown", this.handleKeyDown);
    }

    disconnect(): void {
        document.removeEventListener("click", this.handleDocumentClick, true);
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    closeFromBackdrop(event: MouseEvent): void {
        if (event.target === this.overlayTarget) {
            this.close();
        }
    }

    closeFromAction(event: Event): void {
        event.preventDefault();
        this.close();
    }

    closeBeforeNavigation(): void {
        this.close();
    }

    private open(): void {
        this.previouslyFocusedElement = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        this.loadDesktopQrCodes();
        this.overlayTarget.hidden = false;
        this.overlayTarget.setAttribute("aria-hidden", "false");
        document.body.classList.add("native-app-install-dialog-open");

        requestAnimationFrame(() => {
            this.initialFocusTarget.focus();
        });
    }

    private close(): void {
        this.overlayTarget.hidden = true;
        this.overlayTarget.setAttribute("aria-hidden", "true");
        document.body.classList.remove("native-app-install-dialog-open");

        if (this.previouslyFocusedElement?.isConnected) {
            this.previouslyFocusedElement.focus();
        }
        this.previouslyFocusedElement = null;
    }

    private keepFocusInsideDialog(event: KeyboardEvent): void {
        const focusableElements = Array.from(this.panelTarget.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
        )).filter(element => element.offsetParent !== null || element === document.activeElement);

        if (focusableElements.length === 0) {
            event.preventDefault();
            this.panelTarget.focus();
            return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    private loadDesktopQrCodes(): void {
        if (this.overlayTarget.dataset.nativeAppInstallPlatform === "mobile") {
            return;
        }

        if (typeof window.matchMedia === "function" &&
            !window.matchMedia("(min-width: 56rem)").matches) {
            return;
        }

        for (const image of this.qrCodeTargets) {
            if (image.getAttribute("src")) {
                continue;
            }

            const qrCodeUrl = image.dataset.nativeAppInstallQrSrc;
            if (qrCodeUrl) {
                image.setAttribute("src", qrCodeUrl);
            }
        }
    }
}
